import isSet from "lodash/isSet";
import { useRouter } from "next/router";
import { PropsWithChildren, createContext, useCallback, useEffect, useState } from "react";

import { ColumnSort } from "src/common/types/sorting";
import { noop } from "src/common/utils";
import { decodeUrlData } from "src/common/utils/decodeUrlData";
import { encodeUrlData } from "src/common/utils/encodeUrlData";
import { logError } from "src/common/utils/reporting";
import { BatchRefundFilters } from "src/payments/types/batchRefundFilters";

export type UrlFragmentData = {
  filters?: BatchRefundFilters;
  sort?: ColumnSort[];
  pagination?: {
    after?: string;
    before?: string;
    first: number;
  };
};

export type BatchRefundTableStateContext = {
  batchRefundTableState: UrlFragmentData;
  setBatchRefundTableState: (urlData: UrlFragmentData) => void;
};

const defaultBatchRefundTableStateContext: BatchRefundTableStateContext = {
  batchRefundTableState: {},
  setBatchRefundTableState: () => noop,
};

export const BatchRefundTableStateContext = createContext<BatchRefundTableStateContext>(
  defaultBatchRefundTableStateContext
);

const filterFieldsOfTypeSet = ["statuses", "recipientType"];

export const BatchRefundTableStateProvider = (props: PropsWithChildren<unknown>) => {
  const router = useRouter();
  const dataFromUrl = router.asPath.split("#")[1];

  const [batchRefundTableState, setBatchRefundTableState] = useState<BatchRefundTableStateContext>(
    defaultBatchRefundTableStateContext
  );

  const updateUrlWithBatchRefundTableState = useCallback(
    async (urlData: UrlFragmentData) => {
      try {
        let transformedFilters = urlData.filters;
        if (urlData && urlData.filters) {
          transformedFilters = Object.fromEntries(
            Object.entries(urlData.filters).map(([key, value]) => {
              if (filterFieldsOfTypeSet.includes(key) && isSet(value)) {
                return [key, [...value]];
              }
              return [key, value];
            })
          );
        }

        const encodedUrlData = await encodeUrlData({
          ...urlData,
          filters: transformedFilters,
        });

        await router.replace(`${router.asPath.split("#")[0]}#${encodedUrlData}`);
      } catch (err) {
        logError(err, {
          customMessage: `Unable to encode url data`,
          extraData: { urlData: JSON.stringify({ urlData }) },
        });
      }
    },
    [router]
  );

  const handleSetBatchRefundTableState = (urlData: UrlFragmentData) => {
    setBatchRefundTableState({
      ...batchRefundTableState,
      batchRefundTableState: urlData,
    });
    updateUrlWithBatchRefundTableState(urlData);
  };

  const initializeBatchRefundTableStateFromUrl = useCallback(
    async () => {
      if (!router.isReady) return;
      if (dataFromUrl) {
        const data = await decodeUrlData(dataFromUrl);

        if (data === null || data === undefined) {
          await router.replace(`${router.asPath.split("#")[0]}`);
        } else {
          //@ts-expect-error will sort later
          if (data.filters) {
            //@ts-expect-error will sort later
            data.filters = Object.fromEntries(
              //@ts-expect-error will sort later
              Object.entries(data.filters).map(([key, value]) => {
                if (filterFieldsOfTypeSet.includes(key)) {
                  //@ts-expect-error will sort later
                  return [key, new Set(value)];
                }
                return [key, value];
              })
            );
          }

          setBatchRefundTableState({
            ...batchRefundTableState,
            batchRefundTableState: data,
          });
        }
      }
    },
    // we only care about this value
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.isReady]
  );

  useEffect(() => {
    initializeBatchRefundTableStateFromUrl();
  }, [initializeBatchRefundTableStateFromUrl]);

  return (
    <BatchRefundTableStateContext.Provider
      value={{ ...batchRefundTableState, setBatchRefundTableState: handleSetBatchRefundTableState }}
    >
      {props.children}
    </BatchRefundTableStateContext.Provider>
  );
};
